/**
 * A session's defaults on a cycle or track, as the server works them out
 * (get_session_defaults, migration 1039; roadmap S-40).
 *
 * One walk: track → cycle → workflow (the cycle's, else the workspace's
 * default) → workspace → built-in. Every screen that creates a session or says
 * what a level inherits reads this, so a screen cannot name a value the server
 * does not use. The server applies the same walk when a session is saved.
 */
import { getTypedSupabaseClient } from './typedSupabaseClient';
import type { SessionEntryMode, SessionRunMode, SessionTimingMode } from '../types/prioritization/session';
import type { SessionDefaultsInheritanceBreakdown, SessionDefaultsKeyBreakdown } from './effectiveConfigResolver';

export type SessionDefaultSource = 'track' | 'cycle' | 'workflow' | 'workspace' | 'default';

export interface SessionDefault<T> {
  value: T;
  source: SessionDefaultSource;
}

export interface SessionDefaults {
  runMode: SessionDefault<SessionRunMode>;
  timingMode: SessionDefault<SessionTimingMode>;
  entryMode: SessionDefault<SessionEntryMode>;
  defaultFacilitatorId: SessionDefault<string | null>;
  /** False when a level above says sessions may not change these. */
  allowSessionOverride: SessionDefault<boolean>;
  allowRankOrderChange: SessionDefault<boolean | null>;
  timerAtZero: SessionDefault<'lock' | 'tell' | null>;
  anonymity: SessionDefault<'shown' | 'hidden' | 'withheld' | null>;
  /** The workflow the walk used, and whether it is the workspace's default. */
  workflow: { id: string; name: string | null; workspaceDefault: boolean } | null;
}

/** Where the walk starts: a settings screen asks what its own level inherits. */
export type SessionDefaultsFrom = 'track' | 'cycle' | 'workflow';

type Raw = { value: unknown; source: SessionDefaultSource };

function read<T>(raw: Record<string, Raw> | undefined, key: string, fallback: T): SessionDefault<T> {
  const r = raw?.[key];
  return { value: (r?.value ?? fallback) as T, source: r?.source ?? 'default' };
}

export async function fetchSessionDefaults(
  scope: { cycleId?: string | null; trackId?: string | null },
  from: SessionDefaultsFrom = 'track',
): Promise<{ defaults: SessionDefaults | null; error: string | null }> {
  if (!scope.cycleId && !scope.trackId) return { defaults: null, error: null };
  // A read that fails says nothing: callers go on without it, and the server
  // still applies the same defaults when the session is saved.
  let data: unknown;
  try {
    const res = await getTypedSupabaseClient().rpc('get_session_defaults' as never, {
      p_cycle_id: scope.cycleId ?? null,
      p_track_id: scope.trackId ?? null,
      p_from: from,
    } as never);
    if (res.error) return { defaults: null, error: res.error.message };
    data = res.data;
  } catch (e) {
    return { defaults: null, error: e instanceof Error ? e.message : String(e) };
  }
  const res = data as { success?: boolean; error?: string; defaults?: Record<string, Raw>; workflow?: { id: string; name: string | null; workspace_default: boolean } | null };
  if (!res?.success) return { defaults: null, error: res?.error ?? 'The session defaults could not be read.' };
  const d = res.defaults;
  return {
    error: null,
    defaults: {
      runMode: read<SessionRunMode>(d, 'run_mode', 'facilitator_live'),
      timingMode: read<SessionTimingMode>(d, 'timing_mode', 'live'),
      entryMode: read<SessionEntryMode>(d, 'entry_mode', 'facilitator'),
      defaultFacilitatorId: read<string | null>(d, 'default_facilitator_id', null),
      allowSessionOverride: read<boolean>(d, 'allow_session_override', true),
      allowRankOrderChange: read<boolean | null>(d, 'allow_rank_order_change', null),
      timerAtZero: read<'lock' | 'tell' | null>(d, 'timer_at_zero', null),
      anonymity: read<'shown' | 'hidden' | 'withheld' | null>(d, 'anonymity', null),
      workflow: res.workflow
        ? { id: res.workflow.id, name: res.workflow.name, workspaceDefault: res.workflow.workspace_default }
        : null,
    },
  };
}

/** The title of the box for the level above a cycle. */
export function sessionDefaultAboveTitle(source: SessionDefaultSource, workflowName?: string | null): string {
  switch (source) {
    case 'workflow': return workflowName ? `Workflow: ${workflowName}` : 'Workflow';
    case 'workspace': return 'Workspace';
    default: return 'Standard';
  }
}

/**
 * The cycle and track screens build their breakdown from the levels they hold
 * (their own rows). What sits above the cycle is the server's answer
 * (get_session_defaults from 'workflow'), which includes the workflow — the
 * cycle's, else the workspace's default — that the browser breakdown left out.
 */
export function withServerAbove(
  breakdown: SessionDefaultsInheritanceBreakdown,
  above: SessionDefaults | null,
  facilitatorLabel: (id: string) => string = (id) => id,
): SessionDefaultsInheritanceBreakdown {
  if (!above) return breakdown;
  const workflowName = above.workflow?.name;
  const fill = (entry: SessionDefaultsKeyBreakdown, value: string, source: SessionDefaultSource): SessionDefaultsKeyBreakdown => ({
    ...entry,
    global: value,
    globalLabel: sessionDefaultAboveTitle(source, workflowName),
    effective: entry.source === 'global' ? value : entry.effective,
  });
  const facilitator = above.defaultFacilitatorId.value;
  return {
    ...breakdown,
    timingMode: fill(breakdown.timingMode, above.timingMode.value, above.timingMode.source),
    entryMode: fill(breakdown.entryMode, above.entryMode.value, above.entryMode.source),
    defaultFacilitatorId: fill(breakdown.defaultFacilitatorId, facilitator ? facilitatorLabel(facilitator) : '—', above.defaultFacilitatorId.source),
    effective: {
      ...breakdown.effective,
      ...(breakdown.timingMode.source === 'global' && { timingMode: above.timingMode.value }),
      ...(breakdown.entryMode.source === 'global' && { entryMode: above.entryMode.value }),
      ...(breakdown.defaultFacilitatorId.source === 'global' && { defaultFacilitatorId: facilitator ?? undefined }),
    },
  };
}

/** How a source reads after "from the …". */
export function sessionDefaultSourceLabel(source: SessionDefaultSource, workflowName?: string | null): string {
  switch (source) {
    case 'track': return 'the track';
    case 'cycle': return 'the cycle';
    case 'workflow': return workflowName ? `the ${workflowName} workflow` : 'the workflow';
    case 'workspace': return 'the workspace';
    default: return 'the standard setting';
  }
}
